import {
  Combobox,
  ComboboxOption,
  ComboboxOptions,
  ComboboxInput as HeadlessUIComboboxInput,
} from '@headlessui/react'
import clsx from 'clsx'
import { ControlledFormField, FormFieldCommonProps } from 'lib/types/Form'
import {
  ChangeEvent,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useController } from 'react-hook-form'
import Icon from '../../Icon/Icon'
import InputAddon, { InputAddonProps } from '../InputAddon/InputAddon'
import InputError from '../InputError/InputError'
import InputHint from '../InputHint/InputHint'
import InputLabel from '../InputLabel/InputLabel'

export interface ComboboxInputOption {
  value: string
  label: string
  disabled?: boolean
}

type ComboboxInputStandaloneProps = InputAddonProps &
  Omit<FormFieldCommonProps, 'onChange' | 'defaultValue'> & {
    options: ComboboxInputOption[]
    /**
     * Placeholder text to show when no option is selected yet
     */
    placeholder?: string
    /**
     * Optional classnames for specified children element
     */
    childrenClassNames?: Record<'ComboboxOptions', string>
    onChange?: (data?: string | number, fieldName?: string) => void
    optionFilter?: (option: ComboboxInputOption, query: string) => boolean
    defaultValue?: string
  }

export type ComboboxInputProps = ControlledFormField &
  ComboboxInputStandaloneProps

// Standalone component that can be used outside react-hook-form's context
export const ComboboxInputStandalone = forwardRef<
  HTMLInputElement,
  ComboboxInputStandaloneProps
>(function ComboboxInputStandaloneWithRef(
  {
    name,
    label = '',
    options,
    defaultValue,
    placeholder,
    error,
    onChange,
    disabled = false,
    className,
    hint,
    childrenClassNames,
    optionFilter,
    ...props
  }: ComboboxInputStandaloneProps,
  forwardedRef,
) {
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [isEnabled, setIsEnabled] = useState(
    'conditionalEdit' in props && props.conditionalEdit ? false : !disabled,
  )
  const [selected, setSelected] = useState<ComboboxInputOption['value']>(
    options.find(({ value }) => value === defaultValue)?.value || '',
  )
  const [query, setQuery] = useState('')
  const [inputFocused, setInputFocused] = useState(false)
  const [defaultValueCached, setDefaultValueCached] = useState(defaultValue)

  const queryPattern = useMemo(
    () => new RegExp(query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i'),
    [query],
  )

  const filterOptionByQuery = useCallback(
    (option: ComboboxInputOption) =>
      typeof optionFilter === 'function'
        ? optionFilter(option, query)
        : queryPattern.test(
            typeof option.label === 'string' ? option.label : '',
          ),
    [optionFilter, queryPattern, query],
  )

  const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    // Autocomplete also triggers change event,
    // which HeadlessUI's combobox, in turn, shows dropdown (internal logic)
    // We need to make sure dropdown only shows up when input is focused
    const isInputFocused = event.target === document.activeElement

    if (isInputFocused) {
      setQuery(event.target.value)
    } else {
      const valuePattern = new RegExp(
        event.target.value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
        'i',
      )
      const matchedOption =
        options.find(({ value }) => valuePattern.test(String(value))) ||
        options.find(({ label }) => valuePattern.test(label))

      setQuery(matchedOption?.label || '')
      setSelected((matchedOption?.value as string) || '')
      onChange?.(matchedOption?.value)

      setTimeout(() => {
        inputRef?.current?.blur()
      }, 100)
    }
  }

  const onSelect = useCallback(
    (data: string) => {
      setSelected(data)
      setTimeout(() => {
        inputRef?.current?.blur()
      }, 100)
      onChange?.(data, name)
    },
    [onChange, name],
  )

  const filteredOptions = useMemo(
    () => options.filter(filterOptionByQuery),
    [options, filterOptionByQuery],
  )

  const displayValue = useCallback(
    (data: string) => {
      if (!data) {
        return ''
      }

      const valuePattern = new RegExp(data, 'i')
      const matchedOption = filteredOptions.find((option) =>
        valuePattern.test(String(option.value || '')),
      )

      return matchedOption?.label || ''
    },
    [filteredOptions],
  )

  // Handles defaultValue change from the outside
  useEffect(() => {
    if (
      defaultValue &&
      defaultValue !== defaultValueCached &&
      options?.length
    ) {
      const matchedOption = options.find(({ value }) => value === defaultValue)

      if (inputRef.current) {
        inputRef.current.value =
          displayValue(defaultValue) || matchedOption?.label || ''
      }

      setDefaultValueCached(defaultValue)
      setSelected(defaultValue)
      onChange?.(defaultValue, name)
    }
  }, [
    defaultValue,
    defaultValueCached,
    inputRef,
    onChange,
    name,
    options,
    displayValue,
  ])

  useEffect(() => {
    const wrapperElem = wrapperRef.current
    if (!wrapperElem) return
    const observer = new MutationObserver(() => {
      const options = wrapperElem.querySelector(
        '.comboboxoptions',
      ) as HTMLDivElement
      if (!options) return
      options.removeAttribute('inert')
      if (options.getAttribute('data-headlessui-state') === 'open') {
        if (
          options.offsetHeight >
          document.documentElement.clientHeight -
            wrapperElem.getBoundingClientRect().bottom
        ) {
          options.style.setProperty(
            'max-height',
            `${
              document.documentElement.clientHeight -
              wrapperElem.getBoundingClientRect().bottom
            }px`,
          )
        }
      } else {
        options.style.setProperty('max-height', '')
      }
    })
    observer.observe(wrapperElem, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ['data-headlessui-state'],
    })
    return () => observer.disconnect()
  }, [wrapperRef])

  return (
    <div
      className={clsx('flex flex-wrap relative w-full', className)}
      ref={wrapperRef}
    >
      <div className="flex items-center justify-between w-full">
        {label && <InputLabel className="w-full">{label}</InputLabel>}
        <InputAddon
          {...props}
          {...('conditionalEdit' in props && props.conditionalEdit
            ? { onEdit: setIsEnabled }
            : {})}
        />
      </div>
      <Combobox
        immediate
        as="div"
        name={name}
        className="group relative w-full mt-2 mb-2"
        value={selected}
        onChange={onSelect}
        disabled={!isEnabled}
        defaultValue={defaultValueCached}
        onClose={() => setTimeout(() => setQuery(''))}
      >
        <div className="relative">
          <HeadlessUIComboboxInput
            aria-label={props['aria-label']}
            displayValue={displayValue}
            placeholder={placeholder}
            onChange={onInputChange}
            className={clsx(
              'group flex justify-start items-center h-12 pl-4 pr-10 rounded-8 w-full bg-white type-body-8',
              'lg:h-14',
              'focus:outline-none focus:shadow-input-focus',
              'data-[hover]:shadow-input-active',
              'data-[disabled]:cursor-not-allowed data-[disabled]:bg-neutral-200 data-[disabled]:text-neutral-600 outline-neutral-700',
              'group-data-[disabled]:cursor-not-allowed group-data-[disabled]:bg-neutral-200 group-data-[disabled]:text-neutral-600',
              selected
                ? 'text-neutral-700 shadow-input'
                : 'text-neutral-600 shadow-input',
              error && '!shadow-input-error',
            )}
            onFocus={() => setInputFocused(true)}
            onBlur={() => setInputFocused(false)}
            ref={(element) => {
              inputRef.current = element
              if (typeof forwardedRef === 'function') {
                forwardedRef(element)
              } else if (forwardedRef) {
                forwardedRef.current = element
              }
            }}
          />
          <Icon
            icon="chevron-down"
            className={clsx(
              'absolute top-1/2 right-4 -translate-y-1/2 w-6 h-6 ml-auto text-black pointer-events-none',
              inputFocused && 'group-data-[open]:rotate-180',
            )}
          />
        </div>
        {filteredOptions.length ? (
          <ComboboxOptions
            static={true}
            modal={false}
            className={clsx(
              'comboboxoptions',
              'absolute top-full left-0 translate-y-2 border border-neutral-400 rounded-8 shadow-dropdown bg-white w-full max-h-[280px] overflow-auto z-10 outline-neutral-700',
              childrenClassNames?.ComboboxOptions,
              !inputFocused && 'hidden',
            )}
          >
            {filteredOptions.map((option) => (
              <ComboboxOption
                key={option.value}
                value={option.value}
                disabled={option.disabled ?? false}
                className={clsx(
                  'w-full flex items-center justify-start data-[focus]:bg-neutral-200 px-4 min-h-14 type-body-8',
                  option.disabled
                    ? 'text-neutral-600 bg-neutral-200 cursor-not-allowed'
                    : '',
                )}
              >
                {option.label}
              </ComboboxOption>
            ))}
          </ComboboxOptions>
        ) : null}
      </Combobox>
      <InputHint>{hint}</InputHint>
      <InputError>{error}</InputError>
    </div>
  )
})

// Controlled form element that is automatically registered to react-hook-form
export default function ComboboxInput({
  name,
  label = '',
  control,
  disabled = false,
  className = '',
  options,
  placeholder,
  rules = {},
  onChange,
  shouldUnregister,
  defaultValue,
  ...props
}: ComboboxInputProps) {
  const {
    field: { onChange: fieldOnChange, ...field },
    fieldState,
  } = useController({
    name: name as string,
    control,
    rules,
    shouldUnregister,
    defaultValue,
  })

  // Ensure form field value matches defaultValue on mount.
  // The standalone component skips calling onChange on initial mount
  // (defaultValue === defaultValueCached), so the form field may not
  // be explicitly set through the onChange path. This can cause the
  // form validation to treat the field as empty even though it
  // visually displays the correct value.
  const hasInitRef = useRef(false)
  useEffect(() => {
    if (!hasInitRef.current && defaultValue && options?.length) {
      hasInitRef.current = true
      fieldOnChange(defaultValue)
    }
  }, [defaultValue, fieldOnChange, options?.length])

  const onInputChange = useCallback(
    (data?: number | string) => {
      fieldOnChange?.(data)
      onChange?.(data, name)
    },
    [onChange, fieldOnChange, name],
  )

  return (
    <ComboboxInputStandalone
      {...{
        label,
        options,
        className,
        disabled,
        placeholder,
        rules,
      }}
      {...field}
      {...props}
      defaultValue={defaultValue}
      onChange={onInputChange}
      error={fieldState.error?.message}
    />
  )
}
